// Forecast summary cards — FE-032 / spec §7.0.2.
//
// Three cards: At risk, On track, Underassigned. Each card shows the
// count, a short helper sentence, and a click-through to the
// Commitment Tracker pre-filtered to the matching slice.

import { Link } from "react-router-dom";

import {
  buildCommitmentFilterUrl,
  type DashboardDateRange,
} from "@/features/dashboard";
import type { EmployeeAnalyticsResponse } from "@/features/dashboard";

interface ForecastCardsProps {
  forecast: EmployeeAnalyticsResponse["forecast"];
  dateRange: DashboardDateRange;
}

interface CardConfig {
  key: keyof EmployeeAnalyticsResponse["forecast"];
  label: string;
  helper: string;
  accent: string;
  drillThrough: () => string;
}

export function ForecastCards({ forecast, dateRange }: ForecastCardsProps) {
  const configs: CardConfig[] = [
    {
      key: "at_risk",
      label: "Commitments at risk",
      helper: "Open and overdue, or due within seven days.",
      accent: "text-ember",
      drillThrough: () =>
        buildCommitmentFilterUrl({ overdue: true }, dateRange),
    },
    {
      key: "on_track",
      label: "On track",
      helper: "Open with a due date more than seven days out.",
      accent: "text-sage",
      drillThrough: () =>
        buildCommitmentFilterUrl(
          { has_due_date_time: true, has_assignee: true },
          dateRange,
        ),
    },
    {
      key: "underassigned",
      label: "Underassigned",
      helper: "Open Commitments with no broker staff owner.",
      accent: "text-blocked",
      drillThrough: () =>
        buildCommitmentFilterUrl({ has_assignee: false }, dateRange),
    },
  ];

  return (
    <div
      data-testid="forecast-cards"
      className="grid grid-cols-1 gap-3 sm:grid-cols-3"
    >
      {configs.map((c) => (
        <ForecastCard key={c.key} config={c} value={forecast[c.key]} />
      ))}
    </div>
  );
}

function ForecastCard({
  config, value,
}: {
  config: CardConfig;
  value: number;
}) {
  return (
    <Link
      to={config.drillThrough()}
      data-testid={`forecast-card-${config.key}`}
      aria-label={`${config.label}: ${value}`}
      className="block rounded-lg bg-recessed px-4 py-3 transition-fast ease-out hover:bg-raised"
    >
      <div className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
        {config.label}
      </div>
      <div
        className={`mt-1 text-xl font-semibold ${config.accent}`}
        data-testid={`forecast-value-${config.key}`}
      >
        {value}
      </div>
      <p className="mt-1 text-xs text-text-secondary">{config.helper}</p>
    </Link>
  );
}
